import { apiFetchInit, apiUrl } from "@/lib/apiOrigin";
import { useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { AccessTreeEditor, type AccessDraft } from "@/components/Access/AccessTreeEditor";
import { Badge } from "@/components/ui/badge";
import {
  SystemAccessPanel,
  buildSystemAccessDraft,
  roleHasAllApps,
} from "@/components/Directory/SystemAccessPanel";
import { useDirectoryState } from "@/components/Directory/DirectoryContext";
import {
  DEFAULT_ACCESS_TEMPLATE_ID,
  isAccessTemplateId,
  type ModuleOverrides,
} from "@/lib/accessCatalog";
import { sanitizeStoredSystemAccess } from "@/lib/atwSystemAccess";
import type { GlobalAppSlug } from "@/lib/dashboardAppRegistry";

export function AccessPage() {
  const state = useDirectoryState();
  const ready = state.status === "ready" ? state.value : null;
  const me = ready?.me;
  const org = ready?.org;
  const assignableAppSlugs = ready?.assignableAppSlugs ?? ([] as Exclude<GlobalAppSlug, "home">[]);
  const [moduleAccess, setModuleAccess] = useState<AccessDraft | null>(null);
  const [moduleApps, setModuleApps] = useState<Exclude<GlobalAppSlug, "home">[]>([]);
  const [loading, setLoading] = useState(true);

  const draft = useMemo(() => {
    if (!me) return null;
    return buildSystemAccessDraft({
      role: me.role,
      appEntitlements: me.appEntitlements,
      systemAccess: me.systemAccess,
      orgId: org?.orgId,
      orgMailEnabled: org?.mailEnabled === true,
    });
  }, [me, org?.orgId, org?.mailEnabled]);

  useEffect(() => {
    if (!me?.id) return;
    let cancel = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(apiUrl(`/api/directory/people/${encodeURIComponent(me.id)}`), {
          ...apiFetchInit(),
          cache: "no-store",
        });
        if (!res.ok || cancel) return;
        const data = (await res.json()) as {
          access?: {
            appEntitlements?: GlobalAppSlug[];
            systemAccess?: string[];
            templateId?: string;
            overrides?: ModuleOverrides;
          };
          options?: { apps?: Exclude<GlobalAppSlug, "home">[] };
        };
        if (cancel || !data.access) return;
        const apps = (data.access.appEntitlements || []).filter(
          (slug): slug is Exclude<GlobalAppSlug, "home"> => slug !== "home",
        );
        setModuleAccess({
          systemAccess: sanitizeStoredSystemAccess(data.access.systemAccess) ?? [],
          apps,
          templateId: isAccessTemplateId(data.access.templateId)
            ? data.access.templateId
            : DEFAULT_ACCESS_TEMPLATE_ID,
          overrides: data.access.overrides || {},
        });
        setModuleApps(Array.isArray(data.options?.apps) ? data.options.apps : assignableAppSlugs);
      } catch {
        /* fall back to the system panel */
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => {
      cancel = true;
    };
  }, [me?.id, assignableAppSlugs]);

  if (!ready || !me || !draft) return null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">My access</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Systems you can open, the applications inside them, and what you can do in each module.
        </p>
      </div>

      {loading && !moduleAccess ? (
        <div className="flex justify-center py-16">
          <Loader2 className="size-6 animate-spin text-gray-400" aria-label="Loading" />
        </div>
      ) : moduleAccess ? (
        <div className="space-y-3">
          <AccessTreeEditor
            value={moduleAccess}
            assignableAppSlugs={moduleApps.length > 0 ? moduleApps : assignableAppSlugs}
            fullAccessLocked={roleHasAllApps(me.role)}
            mailAvailable={org?.mailEnabled === true}
            readOnly
          />
          {draft.apps.includes("platform") ? (
            <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-[#1D232D]">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Platform</p>
                  <p className="mt-0.5 text-sm text-gray-600 dark:text-gray-400">
                    ATW operator console (ATW staff only).
                  </p>
                </div>
                <Badge>Can open</Badge>
              </div>
            </div>
          ) : null}
        </div>
      ) : (
        <SystemAccessPanel
          value={draft}
          assignableAppSlugs={assignableAppSlugs}
          appsLocked={roleHasAllApps(me.role)}
          readOnly
        />
      )}
    </div>
  );
}
